// «Начать»: `openTutorialProject` wired to the app — the existing create
// mutations, the personal space, and the hop into the Editor, where the run
// it starts (the core or a chapter) is picked up by the paper's `TutorialRunner`.

import { useAtom, useSetAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApiError } from '@/api/apiUtils';
import { useCreateCharacter } from '@/api/mutations/useCharacterMutations';
import { useCreateFeature } from '@/api/mutations/useFeatureMutations';
import { useCreateProject, useDeleteProject, useForceDeleteProject } from '@/api/mutations/useProjectMutations';
import { useUpdateUserTutorial } from '@/api/mutations/useUserTutorialMutations';
import { useUserQuery } from '@/api/queries/useAuth';
import { tutorialActiveUnitAtom, tutorialRunAtom } from '@/atoms/tutorial';
import { paths } from '@/constants';
import { useCreateFountainFragment, useCreateFountainScene } from '@/data/editorDataLayer';
import { actsEntity } from '@/data/entities/acts';
import { useCompaniesList } from '@/data/entities/companies';
import { fragmentsEntity } from '@/data/entities/fragments';
import { projectsEntity } from '@/data/entities/projects';
import { userTutorialEntity } from '@/data/entities/userTutorial';
import { featureTypeId, ProjectTypeEnum, snackbarColor, textAtomTypeId } from '@/enums';
import { useSnackbar } from '@/hooks';
import { useWorkspace } from '@/hooks/useWorkspace';
import type { TutorialUnit } from '@/types';
import { openTutorialProject, type StoredProjectProbe, type TutorialProjectDeps, TutorialSeedError } from './tutorialProject';
import { buildTutorialSeed } from './tutorialSeed';

/** One open at a time in this page: a second «Начать» (a double click, the
 *  offer card) joins the run in flight instead of seeding twice. Another
 *  browser tab has its own; the stored `project_id` only dedupes runs that
 *  follow one another. */
let inFlight: Promise<{ projectId: number; seeded: boolean }> | null = null;

export function useOpenTutorialProject() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const switchWorkspace = useWorkspace();
    const showSnackbar = useSnackbar();
    const { data: user } = useUserQuery();
    const { data: companies } = useCompaniesList({});

    const { mutateAsync: updateTutorial } = useUpdateUserTutorial();
    const { mutateAsync: createProject } = useCreateProject();
    const { mutateAsync: forceDeleteProject } = useForceDeleteProject();
    const { mutateAsync: deleteProject } = useDeleteProject();
    const { mutateAsync: createFragment } = useCreateFountainFragment();
    const { mutateAsync: createCharacter } = useCreateCharacter();
    const { mutateAsync: createFeature } = useCreateFeature();
    const { mutateAsync: createScene } = useCreateFountainScene();

    const setRun = useSetAtom(tutorialRunAtom);
    const [activeUnit, setActiveUnit] = useAtom(tutorialActiveUnitAtom);
    const [isOpening, setOpening] = useState(false);

    const personalSpace = companies?.find(company => company.is_personal);
    const ready = user !== undefined && companies !== undefined;

    const fail = (message: string) => showSnackbar({ open: true, message, severity: snackbarColor.error });

    const open = async (unit: TutorialUnit = 'core') => {
        if (!user || !companies) return;
        // The project is the user's own whatever company they are in now — and
        // a user without a personal space has nowhere to put it.
        if (!personalSpace) {
            fail(t('tutorial.hub.error-no-personal-space'));
            return;
        }
        const userId = user.id;
        const companyId = personalSpace.id;

        const deps: TutorialProjectDeps = {
            // Straight from the fetcher, past the cache: the decision must not
            // run on a stale `project_id`.
            readTutorial: () => userTutorialEntity.fetchers.get.fetch({ userId }),
            writeProjectId: async projectId => {
                await updateTutorial({ userId, patch: { project_id: projectId } });
            },
            probeProject: async (projectId): Promise<StoredProjectProbe> => {
                try {
                    await projectsEntity.fetchers.detail.fetch({ projectId });
                    return 'active';
                } catch (error) {
                    // The read never serves a trashed project: trashed and
                    // hard-deleted both answer 404, and one the user lost
                    // access to 403. Anything else is not an answer.
                    if (error instanceof ApiError && (error.status === 404 || error.status === 403)) return 'missing';
                    throw error;
                }
            },
            createProject: async title => {
                const data = new FormData();
                data.set('company_id', String(companyId));
                data.set('title', title);
                data.set('episodes_qty', '1');
                // The one act, with its first episode: the backend makes both.
                data.set('act_quantity', '1');
                data.set('type', ProjectTypeEnum.MOVIE);
                return (await createProject(data)).data.id;
            },
            loadStructure: async projectId => {
                const [acts, fragments] = await Promise.all([
                    actsEntity.fetchers.list.fetch({ projectId }),
                    fragmentsEntity.fetchers.list.fetch({ projectId }),
                ]);
                const act = [...acts].sort((a, b) => a.order - b.order)[0];
                if (!act?.episode_id) throw new Error('Tutorial seed: the new project has no act');
                return {
                    episodeId: act.episode_id,
                    actId: act.id,
                    actCode: act.code,
                    fragmentIds: fragments
                        .filter(f => f.act_id === act.id)
                        .sort((a, b) => a.order - b.order)
                        .map(f => f.id),
                };
            },
            createFragment: async (projectId, { actId, code, order }) => {
                const result = await createFragment({
                    projectId,
                    data: { act_id: actId, code, order, title: `${t('fountain.hierarchy.episode')} ${order}` },
                });
                return result.data.id;
            },
            createCharacter: async (projectId, { name, gender, categories }) => {
                const result = await createCharacter({ project_id: projectId, data: { name, gender, categories } });
                return result.data.entity_tag.id;
            },
            createLocation: async (projectId, name) => {
                const result = await createFeature({ project_id: projectId, data: { name, feature_type_id: featureTypeId.location } });
                return result.data.entity_tag.id;
            },
            createScene: async (projectId, { episodeId, fragmentId, position, body, tagIds }) => {
                await createScene({
                    projectId,
                    data: {
                        episode_id: episodeId,
                        fragment_id: fragmentId,
                        position,
                        ...(body === null ? {} : { text_atoms: [{ text_atom_type_id: textAtomTypeId.scene_dialogue, content: body }] }),
                        ...(tagIds.length > 0 && { tags: tagIds }),
                    },
                });
            },
            // The hard delete refuses a project with scenes in it, so a seed
            // that got that far goes to the trash instead — out of the
            // project lists, and never restored (`project_id` was not stored).
            deleteProject: async projectId => {
                await forceDeleteProject({ project_id: projectId }).catch(() => deleteProject({ project_id: projectId }));
            },
        };

        const plan = buildTutorialSeed(t);
        setOpening(true);
        try {
            inFlight ??= openTutorialProject(plan, deps).finally(() => {
                inFlight = null;
            });
            const { projectId } = await inFlight;
            // The unit starts in the Editor, once its paper has the project.
            // It outlives the session: a reload restarts it.
            // The unit was already active — the resume form, or the hub — and is picked up again.
            setRun({ unit, projectId, resumed: activeUnit === unit });
            setActiveUnit(unit);
            switchWorkspace({ companyID: companyId, projectID: projectId });
            navigate(paths.project.scene.editor);
        } catch (error) {
            // biome-ignore lint/suspicious/noConsole: the snackbar says what failed, the console says why
            console.error('[tutorial] open failed:', error);
            if (!(error instanceof TutorialSeedError)) fail(t('tutorial.hub.error-open'));
            // The half-seeded project is still there: say where, or it passes for a real one.
            else if (!error.cleanedUp) fail(t('tutorial.hub.error-seed-leftover', { title: plan.projectTitle }));
            else fail(t('tutorial.hub.error-seed'));
        } finally {
            setOpening(false);
        }
    };

    return { open, isOpening, ready };
}
